import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useVndlyWorkOrderIssuesPaginationQueryState } from '../hooks/useVndlyWorkOrderIssuesPaginationQueryState'
import { useVndlyWorkOrderIssuesQuery } from 'queries/admin/vndly/useVndlyWorkOrderIssuesQuery'
import { useVndlyIssuesFiltersQueryState } from 'pages/VndlyIssues/hooks/useVndlyIssuesFiltersQueryState'

type VndlyWorkOrderIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useVndlyWorkOrderIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<
      typeof useVndlyWorkOrderIssuesPaginationQueryState
    >[1]
  }
}

const [Context, useVndlyWorkOrderIssuesContextValue] =
  createContext<VndlyWorkOrderIssuesContextState>({
    name: 'VndlyWorkOrderIssuesContext',
  })

export const VndlyWorkOrderIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters] = useVndlyIssuesFiltersQueryState()

  const [pagination, setPagination] =
    useVndlyWorkOrderIssuesPaginationQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useVndlyWorkOrderIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    instanceIds: filters.instanceIds,
    companyIds: filters.companyIds,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
    },
    actions: {
      refetch,
      setPagination,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useVndlyWorkOrderIssuesContextValue }
